import { CSSProperties, ReactElement, useEffect, useState } from 'react';

import { useDroppable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconAttachment } from '@components/icons/attachment';
import { IconDirectionRight } from '@components/icons/direction-right';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconErrorSmall } from '@components/icons/error-small';
import { IconSelectMultiple } from '@components/icons/select-multiple';
import { IconSelectSingle } from '@components/icons/select-single';
import { IconTextLong } from '@components/icons/text-long';
import { IconTextShort } from '@components/icons/text-short';
import { IconTrash } from '@components/icons/trash';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { CareerSiteLanguageType } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { FLAG_MAPPER_SMALL } from 'components/iconMappers';
import { JobQuestionMultiLanguage, JobQuestionType } from 'types/jobs';

import { ApplicationFormButtonSegmented, SegmentedButtonType } from './application-form';

import { useSelectSidebar } from '../questions/select-sidebar';
import { useTextFileSidebar } from '../questions/text-file-sidebar';

type JobQuestionVariant = {
  [key in JobQuestionType]: {
    title: string;
    icon: React.ReactNode;
  };
};

type DraggableQuestionProps = {
  question: JobQuestionMultiLanguage;
  index: number;
  languages: CareerSiteLanguageType[];
  removeQuestion: (id: number) => void;
  editQuestion: (editedQuestion: JobQuestionMultiLanguage) => void;
  isDragging?: boolean;
};

export const DraggableQuestion = ({
  question,
  index,
  languages,
  removeQuestion,
  editQuestion,
  isDragging = false,
}: DraggableQuestionProps) => {
  const { t } = useScopedTranslate(questionCardLocale);
  const [expandIndex, setExpandIndex] = useState<string | null>(null);
  const showTextFileSidebar = useTextFileSidebar();
  const showSelectSidebar = useSelectSidebar();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(question.id),
    data: {
      order: index,
    },
  });
  const [required, setRequired] = useState<SegmentedButtonType>(
    question.required ? 'required' : question.hidden ? 'off' : 'optional',
  );

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    cursor: 'grab',
    ...(isDragging && { opacity: 0.5 }),
  };

  useEffect(() => {
    setRequired(question.required ? 'required' : question.hidden ? 'off' : 'optional');
  }, [question]);

  const jobQuestionTitle: JobQuestionVariant = {
    one_line: {
      title: t('shortAnswer'),
      icon: <IconTextShort />,
    },
    multi_line: {
      title: t('paragraph'),
      icon: <IconTextLong />,
    },
    dropdown: {
      title: t('singleSelect'),
      icon: <IconSelectSingle />,
    },
    multidropdown: {
      title: t('multiSelect'),
      icon: <IconSelectMultiple />,
    },
    attachment: {
      title: t('attachment'),
      icon: <IconAttachment />,
    },
  };
  style.cursor = 'grab';

  const toggleExpand = (id: string) => {
    setExpandIndex(expandIndex === null ? id : null);
  };
  const hasOptions =
    (question.type === 'dropdown' || question.type === 'multidropdown') && question.options;

  const setRequirement = (requirement: SegmentedButtonType) => {
    setRequired(requirement);
    editQuestion({
      ...question,
      required: requirement === 'required',
      hidden: requirement === 'off',
    });
  };

  const missingTranslationsTitle = languages.some((l) => question.title[l] === '');

  const missingTranslationsOptions =
    hasOptions &&
    question.options?.some((option) => languages.some((l) => option.display[l] === ''));

  return (
    <div ref={setNodeRef} style={style}>
      <Card space={4} variant="transparent" interactive {...attributes} {...listeners}>
        <Stack space={2}>
          <Stack space={2} orientation="horizontal" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <WrappedIcon icon={<IconDrag color="purple50" />} />
              {jobQuestionTitle[question.type].icon}
              <Text font="semibold14" text={jobQuestionTitle[question.type].title} />
            </Stack>
            <Stack space={2} orientation="horizontal">
              {hasOptions && (
                <IconButton
                  icon={
                    <Rotate isRotated={expandIndex === question.id}>
                      <IconDirectionRight color="purple50" />
                    </Rotate>
                  }
                  variant="ghost-purple"
                  onClick={() => toggleExpand(question.id)}
                />
              )}
              <IconButton
                icon={<IconEdit color="purple50" />}
                variant="ghost-purple"
                onClick={() => {
                  switch (question.type) {
                    case 'one_line':
                    case 'multi_line':
                    case 'attachment':
                      showTextFileSidebar({
                        variant: question.type,
                        languages: languages,
                        question: question,
                        editQuestion: editQuestion,
                      });
                      break;
                    case 'dropdown':
                    case 'multidropdown':
                      showSelectSidebar({
                        variant: question.type,
                        languages: languages,
                        question: question,
                        editQuestion: editQuestion,
                      });
                      break;
                  }
                }}
              />
              <IconButton
                icon={<IconTrash />}
                variant="ghost-purple"
                onClick={() => removeQuestion(index)}
              />
            </Stack>
          </Stack>
          <Stack space={question.type === 'attachment' ? 4 : 2}>
            <Stack space={1}>
              {languages.map((language, index) => (
                <Stack key={index}>
                  <Stack space={1} orientation="horizontal" align="center">
                    {languages.length > 1 ? (
                      <WrappedIcon icon={FLAG_MAPPER_SMALL[language]} />
                    ) : null}
                    <StyledPaddedStack>
                      {Boolean(question.title[language]) ? (
                        <Text
                          style={{ wordBreak: 'break-word' }}
                          text={question.title[language]}
                          font="regular14"
                        />
                      ) : (
                        <Text text={t('noTranslation')} font="regular14" color="purple50" />
                      )}
                    </StyledPaddedStack>
                  </Stack>
                </Stack>
              ))}
              {(missingTranslationsTitle || missingTranslationsOptions) && (
                <Stack orientation="horizontal" space={1} align="center">
                  <WrappedIcon icon={<IconErrorSmall color="error" />} />
                  <Text
                    font="regular14"
                    color="red100"
                    text={
                      missingTranslationsOptions && !missingTranslationsTitle
                        ? t('noOptionTranslation')
                        : t('langError')
                    }
                  />
                </Stack>
              )}
            </Stack>
            {question.type === 'attachment' && (
              <StyledPaddedStack orientation="horizontal" justify="space-between">
                <Text text={t('allowMultiple')} font="regular14" />
                <Switch
                  label={t('allowMultiple')}
                  labelVisible={false}
                  value={question.multiple}
                  onChange={() =>
                    editQuestion({
                      ...question,
                      multiple: !question.multiple,
                    })
                  }
                />
              </StyledPaddedStack>
            )}
            <Collapse isCollapsed={question.id !== expandIndex}>
              <Stack space={4}>
                <Separator />
                <Stack space={4}>
                  {question.options?.map((option, index) => (
                    <Stack space={1} key={index}>
                      {languages.map((language, index) => (
                        <Stack key={index}>
                          <StyledPaddedStack space={1} orientation="horizontal" align="center">
                            {languages.length > 1 ? FLAG_MAPPER_SMALL[language] : null}
                            <Stack>
                              {Boolean(option.display[language]) ? (
                                <Text text={option.display[language]} font="regular14" />
                              ) : (
                                <Text text={t('noTranslation')} font="regular14" color="purple50" />
                              )}
                            </Stack>
                          </StyledPaddedStack>
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
                <StyledPaddedStack>
                  {missingTranslationsOptions && (
                    <Stack orientation="horizontal" space={1} align="center">
                      <StyledError align="center" justify="center">
                        <IconErrorSmall color="error" />
                      </StyledError>
                      <Text font="regular14" color="red100" text={t('langError')} />
                    </Stack>
                  )}
                </StyledPaddedStack>
              </Stack>
            </Collapse>
            <Stack align="end">
              <ApplicationFormButtonSegmented selected={required} setSelected={setRequirement} />
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const StyledPaddedStack = styled(Stack)({
  padding: `${pxToRem(0)} ${pxToRem(8)}`,
});

const StyledError = styled(Stack)({
  paddingLeft: pxToRem(4),
});

const StyledIcon = styled(Stack)({
  width: pxToRem(40),
  minWidth: pxToRem(40),
});

const WrappedIcon = ({ icon }: { icon: ReactElement<IconProps> }) => {
  return (
    <StyledIcon align="center" justify="center">
      {icon}
    </StyledIcon>
  );
};

type QuestionCardProps = {
  question: JobQuestionMultiLanguage;
  index: number;
  languages: CareerSiteLanguageType[];
};

export const QuestionCard = (props: QuestionCardProps) => {
  const { question, index, languages } = props;
  const { t } = useScopedTranslate(questionCardLocale);
  const { setNodeRef } = useDroppable({
    id: String(question.id),
    data: {
      order: index,
    },
  });

  const jobQuestionTitle: JobQuestionVariant = {
    one_line: {
      title: t('shortAnswer'),
      icon: <IconTextShort />,
    },
    multi_line: {
      title: t('paragraph'),
      icon: <IconTextLong />,
    },
    dropdown: {
      title: t('singleSelect'),
      icon: <IconSelectSingle />,
    },
    multidropdown: {
      title: t('multiSelect'),
      icon: <IconSelectMultiple />,
    },
    attachment: {
      title: t('attachment'),
      icon: <IconAttachment />,
    },
  };

  const hasOptions = question.type === 'dropdown' || question.type === 'multidropdown';
  const missingTranslationsTitle = languages.some((language) => !question.title[language]);

  return (
    <div ref={setNodeRef}>
      <Card space={4} variant="transparent" style={{ background: theme.color.purple5 }}>
        <Stack space={2}>
          <Stack space={2} orientation="horizontal" justify="space-between">
            <Stack space={2} orientation="horizontal" align="center">
              <WrappedIcon icon={<IconDrag color="purple50" />} />
              {jobQuestionTitle[question.type].icon}
              <Text font="semibold14" text={jobQuestionTitle[question.type].title} />
            </Stack>
            <Stack space={2} orientation="horizontal">
              {hasOptions && (
                <IconButton icon={<IconDirectionRight color="purple50" />} variant="ghost-purple" />
              )}
              <IconButton icon={<IconEdit color="purple50" />} variant="ghost-purple" />
              <IconButton icon={<IconTrash />} variant="ghost-purple" />
            </Stack>
          </Stack>
          <Stack space={1}>
            {languages.map((language, index) => (
              <Stack key={index}>
                <Stack space={1} orientation="horizontal" align="center">
                  {languages.length > 1 ? <WrappedIcon icon={FLAG_MAPPER_SMALL[language]} /> : null}
                  <StyledPaddedStack>
                    {Boolean(question.title[language]) ? (
                      <Text
                        text={question.title[language]}
                        font="regular14"
                        style={{ wordBreak: 'break-word' }}
                      />
                    ) : (
                      <Text text={t('noTranslation')} font="regular14" color="purple50" />
                    )}
                  </StyledPaddedStack>
                </Stack>
              </Stack>
            ))}
            {missingTranslationsTitle && (
              <Stack orientation="horizontal" space={1} align="center">
                <WrappedIcon icon={<IconErrorSmall color="error" />} />
                <Text font="regular14" color="red100" text={t('langError')} />
              </Stack>
            )}
          </Stack>
          {question.type === 'attachment' && (
            <StyledPaddedStack orientation="horizontal" justify="space-between">
              <Text text={t('allowMultiple')} font="regular14" />
              <Switch
                label={t('allowMultiple')}
                labelVisible={false}
                defaultValue={question.multiple}
                onChange={() => {}}
              />
            </StyledPaddedStack>
          )}
          <Stack align="end">
            <ApplicationFormButtonSegmented
              selected={question.required ? 'required' : question.hidden ? 'off' : 'optional'}
              setSelected={() => {}}
            />
          </Stack>
        </Stack>
      </Card>
    </div>
  );
};

const questionCardLocale = {
  en: {
    shortAnswer: 'Text - Short answer',
    paragraph: 'Text - Long answer',
    singleSelect: 'Select - Single select',
    multiSelect: 'Select - Multi select',
    attachment: 'Attachment - File upload',
    edit: 'Edit question',
    delete: 'Delete question',
    langError: 'Translation missing',
    noTranslation: 'No translation',
    noOptionTranslation: 'Translation missing on options',
    allowMultiple: 'Allow multiple attachments',
  },
  is: {
    shortAnswer: 'Texti - Stutt svar',
    paragraph: 'Texti - Langt svar',
    singleSelect: 'Fellilisti - Eitt val',
    multiSelect: 'Fellilisti - Fjölval',
    attachment: 'Viðhengi - Hlaða upp skrá',
    edit: 'Breyta spurningu',
    delete: 'Eyða spurningu',
    langError: 'Þýðingu vantar',
    noTranslation: 'Engin þýðing',
    noOptionTranslation: 'Þýðingu vantar á valkostum',
    allowMultiple: 'Leyfa margar skrár',
  },
};
